import { WithStoryProps } from '@storyofams/storyblok-toolkit';
import { format } from 'date-fns';
import { ArticleJsonLd } from 'next-seo';
import SbEditable from 'storyblok-react';
import { GuidechapterItems } from '~/graphql/sdk';
import { classNames, getDescription, getLinkProps } from '~/lib';
import { DynamicPage, Heading, Link } from '..';
import { BannerDynamic } from '../Banners/BannerDynamic';
import { Faq } from '../Blocks/Faq';
import { ArrowBullet } from '../Icons';
import { Share } from '../Share';
import { PageNavigation } from './PageNavigation';

export interface GuideIndexPageProps extends WithStoryProps {
  story: any;
  slug: string;
  guideChapters?: GuidechapterItems;
}

export const GuideIndexPage = ({
  story,
  slug,
  guideChapters,
}: GuideIndexPageProps) => {
  const previous = {
    content: { title: 'Blog overview' },
    full_slug: 'blog',
  };
  return (
    <div className="bg-dark-grey-700 overflow-visible mb-48">
      <ArticleJsonLd
        url={`https://instantcommerce.io${slug}`}
        title={story?.content?.title}
        description={getDescription(story)}
        datePublished={story?.first_published_at}
        dateModified={story?.published_at}
        images={
          story?.content?.thumbnail?.filename
            ? [story?.content?.thumbnail?.filename]
            : []
        }
        authorName="Instant Commerce"
        publisherName="Instant Commerce"
        publisherLogo=""
      />

      <div className="max-w-5xl mx-auto">
        {story?.content?.title && (
          <div className="pt-20 lg:pt-36 px-8 md:px-4 lg:px-28">
            <div className="md:px-2.5">
              <div className="flex w-full justify-between items-center mb-10">
                <div className="bg-white rounded-full px-3 py-1 uppercase font-bold text-sm">
                  guide
                </div>
                <div className="text-white uppercase text-sm">
                  {format(new Date(story?.published_at), 'dd MMMM yyyy')}
                </div>
              </div>
              <Heading variant="h2" as="h1" className="text-white">
                {story?.content?.title}
              </Heading>
            </div>
          </div>
        )}

        <DynamicPage story={story} className="lg:px-2.5 pb-10 mt-4" />

        <div className="px-4 md:px-28">
          <div className="px-4 md:px-2.5">
            {guideChapters?.items?.map((chapter) => (
              <Link
                key={chapter?.uuid}
                href={`/${getLinkProps(chapter?.full_slug).replace(
                  'guides/',
                  '',
                )}`}
                className="text-yellow-100 text-lg hover:text-brand focus:text-brand flex mt-6 first:mt-0 group"
              >
                <ArrowBullet className="w-4 mr-4 h-7 text-white group-hover:transform group-hover:translate-x-2 transition-transform group-hover:text-brand" />
                {chapter?.content?.title}
              </Link>
            ))}

            <PageNavigation
              className="mt-16"
              previous={previous}
              next={guideChapters?.items?.[0]}
              index
            />
          </div>
          <div className="flex flex-col items-center justify-center mt-20 md:mt-24">
            <Share title="sharing" white center />
          </div>
        </div>
      </div>
      <div
        className={classNames(
          'w-full max-w-6xl mx-auto px-4 md:px-5 pt-0 lg:pt-0 lg:max-w-6xl',
          'relative top-32',
        )}
      >
        <Faq
          kicker={story?.content?.faq_kicker}
          title={story?.content?.faq_title}
          questions={story?.content?.faq}
          className="mb-32"
          darkMode
        />

        {story?.content?.banner?.content && (
          <SbEditable content={story.content.banner.content}>
            <BannerDynamic content={story.content.banner?.content} />
          </SbEditable>
        )}
      </div>
    </div>
  );
};
